import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { formatPhoneNumberDisplay } from '../../../lib/utils'
import { BookingDataTemplate } from '../../../Bookings/utils/booking-data-template'
import { executeBooking, registerBooking } from '../../../Bookings/utils/bpm-utils'
import store from '../../../../store'
import { createBooking } from '../../../Bookings/utils/utils'
import coreReducer from '../../../../core/utils/reducer'
import PatientExaminationForm from '../../../Bookings/components/ExaminationWrapper'
import CoreEnums from '../../../../core/utils/enums'
import { message } from 'antd'
import { handleError } from '../../../lib/errorHandler'

const useSystemUsersTable = ({ type, clinics }) => {
  const [viewType, setViewType] = useState(type)
  const [data, setData] = useState([])
  const state = useSelector((state) => state)
  const coreState = useSelector((state) => state.core)
  const dispatch = useDispatch()

  const systemUsersData = useSelector((state) => state.systemUsers.data)
  const medicalProfessionalsData = useSelector((state) => state.medicalProfessionals.data)
  const patientsData = useSelector((state) => state.patients.data)
  const [tableData, setTableData] = useState()
  const [teamChatProps, setTeamChatProps] = useState({ isOpen: false, userRef: '' })
  const [examinationModalOpen, setExaminationModalOpen] = useState(false)

  const [userFilters, setUserFilters] = useState({
    firstName: [],
    lastName: [],
    email: []
  })

  const beginAdhocScreening = async (bookingData, patientRecord) => {
    // setLoading(true)
    const clinic = JSON.parse(bookingData.clinic)
    let record = BookingDataTemplate(patientRecord, clinic, {
      chiefComplaint: bookingData.ailment,
      ailment: bookingData.ailment,
      patientSelection: bookingData.patientSelection,
      patientInput: bookingData.patientInput,
      paymentMethod: 'No Appointment',
      service: state.auth.agiliteUser.extraData.services[0],
      processRef: null,
      status: null
    })
    let bpmProcess = null
    let processUser = `${state.auth.agiliteUser.firstName} ${state.auth.agiliteUser.lastName}`

    try {
      bpmProcess = await registerBooking(undefined, processUser)

      if (store.getState().auth.agiliteUser.extraData.profession === 'doctor') {
        // New To Diagnosis (Skipping Checkin and Screening)
        bpmProcess = await executeBooking(
          'patient_journey',
          bpmProcess.recordId,
          'submit',
          processUser,
          bpmProcess.key,
          'consultation'
        )
      } else {
        // New To Screening (Skipping Checkin)
        bpmProcess = await executeBooking(
          'patient_journey',
          bpmProcess.recordId,
          'submit',
          processUser,
          bpmProcess.key,
          'screening'
        )
      }

      record.processRef = bpmProcess.recordId
      record.status = clinic.bpmConfig?.find((e) => e.key === bpmProcess.key)?.key

      record = { ...record, ...(await createBooking(record)) }

      dispatch(
        coreReducer.actions.addTab({
          key: `${CoreEnums.tabKeys.MY_BOOKINGS}_${record._id}`,
          closable: true,
          label: `Booking: ${record.patientName}`,
          children: <PatientExaminationForm data={record} noAppointment={true} />
        })
      )
      setExaminationModalOpen(false)
    } catch (e) {
      message.error(handleError(e, true))
    }
    // setLoading(false)
  }

  // Function to format phone numbers in user data
  const formatUserDataPhoneNumbers = (userData) => {
    if (!userData || !Array.isArray(userData)) return userData

    return userData.map((user) => {
      const formattedUser = { ...user }

      // Format phoneNumber field if it exists
      if (formattedUser.phoneNumber) {
        formattedUser.phoneNumber = formatPhoneNumberDisplay(formattedUser.phoneNumber)
      }

      return formattedUser
    })
  }

  useEffect(() => {
    let formattedData

    switch (type) {
      case 'system':
        setViewType('System Users')
        formattedData = formatUserDataPhoneNumbers(systemUsersData)
        setTableData(formattedData)
        setData(formattedData)
        break
      case 'medical':
        setViewType('Medical Professionals')
        formattedData = formatUserDataPhoneNumbers(medicalProfessionalsData)
        setTableData(formattedData)
        setData(formattedData)
        break
      case 'patient':
        setViewType('Patients')
        formattedData = formatUserDataPhoneNumbers(patientsData)
        setTableData(formattedData)
        setData(formattedData)
        break
      default:
        setViewType('System Users')
        formattedData = formatUserDataPhoneNumbers(systemUsersData)
        setTableData(formattedData)
        setData(formattedData)
    }
    // eslint-disable-next-line
  }, [systemUsersData, medicalProfessionalsData, patientsData])

  useEffect(() => {
    generateFilters()
    // eslint-disable-next-line
  }, [data])

  const generateFilters = () => {
    let tmpRoles = JSON.parse(JSON.stringify(state.auth.roles))
    let tmpProfessions = JSON.parse(JSON.stringify(state.auth.professions))
    let tmpClinics = clinics
    let tmpServices = [...JSON.parse(JSON.stringify(state.services.data))]
    if (state.core.entity.virtualConsultations.enabled) {
      state.virtualServices.data.forEach((service) => {
        let tmpService = JSON.parse(JSON.stringify(service)) // create instance
        if (tmpService) {
          tmpService.name = `${service.name} (Virtual)`
          tmpServices.push(tmpService)
        }
      })
    }

    tmpRoles = tmpRoles.filter((i) => i.value !== 'patient' && i.value !== 'medical_professional')
    const filters = {
      roles: tmpRoles.map((role) => {
        return {
          text: role.label,
          value: role.value
        }
      }),
      professions: tmpProfessions.map((role) => {
        return {
          text: role.label,
          value: role.value
        }
      }),
      services: tmpServices.map((service) => {
        return {
          text: service.name,
          value: service._id
        }
      }),
      clinics: tmpClinics.map((clinic) => {
        return {
          text: clinic.name,
          value: clinic._id
        }
      })
    }

    setUserFilters(filters)
  }
  return {
    viewType,
    setViewType,
    tableData,
    setTableData,
    data,
    setData,
    userFilters,
    generateFilters,
    teamChatProps,
    setTeamChatProps,
    state,
    coreState,
    dispatch,
    systemUsersData,
    medicalProfessionalsData,
    patientsData,
    setExaminationModalOpen,
    examinationModalOpen,
    beginAdhocScreening
  }
}

export default useSystemUsersTable
